import React, { useEffect, useState } from 'react';
import { geoLocationsApi } from '../lib/api-client';
import useLocale from '../contexts/LocaleContext';
import { Input } from './ui/Input';

// Types for location data
interface LocationItem {
  id: string;
  name: string;
  locationType: string;
}

export interface LocationDetails {
  provinceId?: string | null;
  districtId?: string | null;
  sectorId?: string | null;
  cellId?: string | null;
  villageId?: string | null;
}

export type LocationSelectorLayout = 'column' | 'row' | 'grid-2' | 'grid-3';

interface LocationSelectorProps {
  locationDetails: LocationDetails;
  setLocationDetails: (data: LocationDetails) => void;
  /** Class applied to the outer container. */
  className?: string;
  /** Class applied to each individual field wrapper. */
  fieldClassName?: string;
  hideLabels?: boolean;
  /** Preset layout for the container: column (default), row, grid-2, grid-3. */
  layout?: LocationSelectorLayout;
  restrictToParent?: LocationDetails;
  /** Fired whenever the deepest selected location changes. */
  onDeepestChange?: (location: { id: string; name: string } | null) => void;
}

const LAYOUT_CLASS: Record<LocationSelectorLayout, string> = {
  column: 'grid grid-cols-1 gap-3',
  row: 'flex flex-row flex-wrap gap-3 items-end',
  'grid-2': 'grid grid-cols-2 gap-3',
  'grid-3': 'grid grid-cols-3 gap-3',
};

const LocationSelector: React.FC<LocationSelectorProps> = ({
  locationDetails,
  setLocationDetails,
  className,
  fieldClassName,
  hideLabels,
  layout = 'column',
  restrictToParent,
  onDeepestChange,
}) => {
  const { dictionary } = useLocale();
  const [provinces, setProvinces] = useState<LocationItem[]>([]);
  const [districts, setDistricts] = useState<LocationItem[]>([]);
  const [sectors, setSectors] = useState<LocationItem[]>([]);
  const [cells, setCells] = useState<LocationItem[]>([]);
  const [villages, setVillages] = useState<LocationItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (restrictToParent) {
      if (restrictToParent.cellId) {
        fetchSubdivisions(restrictToParent.cellId, 'village');
      } else if (restrictToParent.sectorId) {
        fetchSubdivisions(restrictToParent.sectorId, 'cell');
      } else if (restrictToParent.districtId) {
        fetchSubdivisions(restrictToParent.districtId, 'sector');
      } else if (restrictToParent.provinceId) {
        fetchSubdivisions(restrictToParent.provinceId, 'district');
      }
    } else {
      fetchProvinces();
    }
  }, [restrictToParent]);

  useEffect(() => {
    if (locationDetails.provinceId) fetchSubdivisions(locationDetails.provinceId, 'district');
  }, [locationDetails.provinceId]);

  useEffect(() => {
    if (locationDetails.districtId) fetchSubdivisions(locationDetails.districtId, 'sector');
  }, [locationDetails.districtId]);

  useEffect(() => {
    if (locationDetails.sectorId) fetchSubdivisions(locationDetails.sectorId, 'cell');
  }, [locationDetails.sectorId]);

  useEffect(() => {
    if (locationDetails.cellId) fetchSubdivisions(locationDetails.cellId, 'village');
  }, [locationDetails.cellId]);

  // Notify parent about the deepest selected location
  useEffect(() => {
    if (!onDeepestChange) return;
    const find = (list: LocationItem[], id: string | null | undefined) =>
      id ? list.find(l => l.id === id) ?? null : null;

    let found: LocationItem | null = null;
    if (locationDetails.villageId) found = find(villages, locationDetails.villageId);
    else if (locationDetails.cellId) found = find(cells, locationDetails.cellId);
    else if (locationDetails.sectorId) found = find(sectors, locationDetails.sectorId);
    else if (locationDetails.districtId) found = find(districts, locationDetails.districtId);
    else if (locationDetails.provinceId) found = find(provinces, locationDetails.provinceId);

    onDeepestChange(found ? { id: found.id, name: found.name } : null);
  }, [locationDetails, provinces, districts, sectors, cells, villages, onDeepestChange]);

  const fetchProvinces = async () => {
    setLoading(true);
    try {
      const response = await geoLocationsApi.getProvinces();
      setProvinces(response);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubdivisions = async (parentId: string, level: string) => {
    setLoading(true);
    try {
      const response = await geoLocationsApi.getAddressInfo(parentId);
      const subLocations: LocationItem[] = response.subLocations?.data || [];
      switch (level) {
        case 'district': setDistricts(subLocations); setSectors([]); setCells([]); setVillages([]); break;
        case 'sector':   setSectors(subLocations);   setCells([]);   setVillages([]);                break;
        case 'cell':     setCells(subLocations);     setVillages([]);                                break;
        case 'village':  setVillages(subLocations);                                                  break;
      }
    } catch (error) {
      console.error(`Error fetching ${level}s:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (level: keyof LocationDetails, id: string) => {
    switch (level) {
      case 'provinceId':
        setLocationDetails({ ...locationDetails, provinceId: id || null, districtId: null, sectorId: null, cellId: null, villageId: null });
        break;
      case 'districtId':
        setLocationDetails({ ...locationDetails, districtId: id || null, sectorId: null, cellId: null, villageId: null });
        break;
      case 'sectorId':
        setLocationDetails({ ...locationDetails, sectorId: id || null, cellId: null, villageId: null });
        break;
      case 'cellId':
        setLocationDetails({ ...locationDetails, cellId: id || null, villageId: null });
        break;
      case 'villageId':
        setLocationDetails({ ...locationDetails, villageId: id || null });
        break;
    }
  };

  const renderSelect = (
    level: keyof LocationDetails,
    items: LocationItem[],
    label: string,
    placeholder: string,
    disabled = false
  ) => (
    <div className={fieldClassName}>
      <Input
        type='select'
        label={hideLabels ? undefined : label}
        options={items.map(i => ({ value: i.id, label: i.name }))}
        value={locationDetails[level] ?? ''}
        onChange={e => handleLevelChange(level, String(e.target.value))}
        placeholder={placeholder}
        disabled={loading || disabled}
      />
    </div>
  );

  const isProvinceDisabled = !!(restrictToParent?.provinceId);
  const isDistrictDisabled = !!(restrictToParent?.districtId);
  const isSectorDisabled   = !!(restrictToParent?.sectorId);
  const isCellDisabled     = !!(restrictToParent?.cellId);

  return (
    <div className={`${LAYOUT_CLASS[layout]} w-full ${className ?? ''}`}>
      {(!restrictToParent || !restrictToParent.provinceId)
        ? renderSelect('provinceId', provinces, dictionary['form.province'], dictionary['location.select_province'])
        : renderSelect(
            'provinceId',
            [{ id: restrictToParent.provinceId, name: locationDetails.provinceId || '', locationType: 'PROVINCE' }],
            dictionary['form.province'],
            dictionary['location.select_province'],
            true
          )}

      {locationDetails.provinceId && !restrictToParent?.districtId &&
        renderSelect('districtId', districts, dictionary['form.district'], dictionary['location.select_district'], isDistrictDisabled)}
      {restrictToParent?.districtId &&
        renderSelect(
          'districtId',
          [{ id: restrictToParent.districtId, name: locationDetails.districtId || '', locationType: 'DISTRICT' }],
          dictionary['form.district'],
          dictionary['location.select_district'],
          true
        )}

      {locationDetails.districtId && !restrictToParent?.sectorId &&
        renderSelect('sectorId', sectors, dictionary['form.sector'], dictionary['location.select_sector'], isSectorDisabled)}
      {restrictToParent?.sectorId &&
        renderSelect(
          'sectorId',
          [{ id: restrictToParent.sectorId, name: locationDetails.sectorId || '', locationType: 'SECTOR' }],
          dictionary['form.sector'],
          dictionary['location.select_sector'],
          true
        )}

      {locationDetails.sectorId && !restrictToParent?.cellId &&
        renderSelect('cellId', cells, dictionary['form.cell'], dictionary['location.select_cell'], isCellDisabled)}
      {restrictToParent?.cellId &&
        renderSelect(
          'cellId',
          [{ id: restrictToParent.cellId, name: locationDetails.cellId || '', locationType: 'CELL' }],
          dictionary['form.cell'],
          dictionary['location.select_cell'],
          true
        )}

      {locationDetails.cellId &&
        renderSelect('villageId', villages, dictionary['form.village'], dictionary['location.select_village'])}
    </div>
  );
};

export default LocationSelector;
